import { useEffect, useState } from "react";
import api, { fmtINR, fmtDT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, X, ShieldCheck, Clock, XCircle } from "lucide-react";
import { usePerms } from "@/context/PermContext";

const TABS = [
  { key: "pending", label: "Pending", icon: Clock, tone: "amber" },
  { key: "approved", label: "Approved", icon: ShieldCheck, tone: "teal" },
  { key: "rejected", label: "Rejected", icon: XCircle, tone: "red" },
];

export default function DiscountApprovals() {
  const { can } = usePerms();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });

  const load = () => {
    api.get("/discount-requests", { params: { status: tab } }).then((r) => setRows(r.data));
    // load counts
    Promise.all(TABS.map((t) => api.get("/discount-requests", { params: { status: t.key } }))).then((res) => {
      const c = {};
      TABS.forEach((t, i) => { c[t.key] = res[i].data.length; });
      setCounts(c);
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const canApprove = can("billing", "approve");

  const approve = async (r) => {
    if (!window.confirm(`Approve discount of ₹${r.amount} for ${r.patient_name || r.patient_id}?`)) return;
    try {
      await api.put(`/discount-requests/${r.id}/approve`, { reason: "" });
      toast.success("Discount approved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reject = async (r) => {
    const reason = window.prompt("Reason for rejection?");
    if (reason === null) return;
    try {
      await api.put(`/discount-requests/${r.id}/reject`, { reason });
      toast.success("Discount rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-semibold">Discount Approvals</h1>
        <p className="text-sm text-slate-500">Reception-raised discount requests. Only Admin / Billing manager can approve.</p>
      </div>

      <div className="flex gap-2 border-b">
        {TABS.map((t) => {
          const Ic = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`approvals-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px transition ${
                active
                  ? `border-${t.tone}-600 text-${t.tone}-700 font-semibold`
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Ic className="w-4 h-4" />
              {t.label}
              <span className="ml-1 text-xs bg-slate-100 px-1.5 rounded-sm">{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white border">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left">
            <th className="label-tiny p-2">Requested</th>
            <th className="label-tiny">Patient</th>
            <th className="label-tiny">UHID</th>
            <th className="label-tiny">Ref</th>
            <th className="label-tiny">Reason</th>
            <th className="label-tiny">Requested By</th>
            <th className="label-tiny text-right">Amount</th>
            <th className="label-tiny">Status</th>
            {tab === "pending" && <th className="label-tiny text-right pr-3">Action</th>}
            {tab !== "pending" && <th className="label-tiny">Decided</th>}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b" data-testid={`approval-row-${r.id}`}>
                <td className="p-2 text-xs">{fmtDT(r.requested_at)}</td>
                <td className="font-medium">{r.patient_name || "-"}</td>
                <td className="font-mono text-xs">{r.uhid || "-"}</td>
                <td className="font-mono text-[11px]">{r.opd_id ? "OPD" : r.ipd_id ? "IPD" : "-"}</td>
                <td className="text-xs text-slate-600">{r.reason || "-"}</td>
                <td className="text-xs">{r.requested_by_name || r.requested_by}</td>
                <td className="text-right font-mono">{fmtINR(r.amount)}</td>
                <td>
                  <Badge className={`rounded-sm ${r.status === "approved" ? "bg-teal-100 text-teal-800" : r.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{r.status}</Badge>
                </td>
                {tab === "pending" && (
                  <td className="text-right pr-3">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" onClick={() => approve(r)} disabled={!canApprove} data-testid={`approve-${r.id}`} className="h-7 bg-teal-700 hover:bg-teal-800"><Check className="w-3 h-3 mr-1"/> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => reject(r)} disabled={!canApprove} data-testid={`reject-${r.id}`} className="h-7 border-red-300 text-red-700 hover:bg-red-50"><X className="w-3 h-3 mr-1"/> Reject</Button>
                    </div>
                  </td>
                )}
                {tab !== "pending" && (
                  <td className="text-xs">
                    <div>{r.decided_by_name || "-"}</div>
                    <div className="text-slate-500 text-[11px]">{fmtDT(r.decided_at)}</div>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={tab === "pending" ? 9 : 9} className="p-6 text-center text-sm text-slate-500">No {tab} discount requests</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
